// Bereich einer gesperrten Funktion, etwa das Attract-Formular oder die
// Weiterleitung. Wirkt nur in enforced, in legacy und preview rendert er die
// Kinder unverändert, dort bleibt die heutige Paywall des Aufrufers.
//
// Nicht in Nutzung liegt der Inhalt inert und blass unter einem Button
// „Freischalten", der den Upgrade-Dialog öffnet. Kein Blur, bei Settings sind
// es die eigenen Werte des Kunden.
// In Nutzung läuft die Funktion weiter. Der Inhalt bleibt lesbar, ist aber
// nicht bearbeitbar (disabled fieldset), darüber steht ein Hinweis mit Pille.
// Abschalten geht immer. Die Abschalt-Aktion (Attract aus, Weiterleitung
// entfernen, Auf Standard zurücksetzen) reicht der Aufrufer als allowedAction
// herein, sie steht außerhalb des gesperrten Inhalts. Mit
// allowedActionPosition top steht sie direkt unter dem Hinweis, etwa der
// Schalter von Attract über einem langen Formular, sonst darunter.
import { LockClosedIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { FEATURES } from '../../features/pricing/plans'
import { classNames } from '../../helpers'
import InfoCallout from '../tailwind/InfoCallout'
import LockBadge, { LockPill, useLockTexts } from './lock-badge'

const LockedSection = ({
  feature,
  source,
  inUse,
  allowedAction,
  allowedActionPosition,
  className,
  children
}) => {
  const { t } = useTranslation()
  const { gate, label, ariaLabel, showPlanNames } = useLockTexts(feature, {
    source,
    inUse
  })

  if (!gate.locked) return children

  if (gate.readOnly) {
    const action = allowedAction ? (
      <div className='flex flex-wrap items-center gap-3'>{allowedAction}</div>
    ) : null
    const actionOnTop = allowedActionPosition === 'top'

    return (
      <div className={classNames('space-y-4', className)}>
        <InfoCallout
          tone='neutral'
          icon={LockClosedIcon}
          action={
            <LockBadge feature={feature} source={source} inUse size='xs' />
          }
        >
          {t('pricing.dialog.in_use_hint')}
          {allowedAction && ` ${t('pricing.dialog.off_allowed_hint')}`}
        </InfoCallout>
        {actionOnTop && action}
        {/* min-w-0, sonst setzt fieldset min-width auf min-content */}
        <fieldset disabled className='min-w-0' data-pricing-read-only=''>
          {children}
        </fieldset>
        {!actionOnTop && action}
      </div>
    )
  }

  return (
    <div className={classNames('relative', className)}>
      {/* React 18 kennt inert nicht als Boolean, deshalb als String */}
      <div
        inert=''
        aria-hidden='true'
        className='pointer-events-none select-none opacity-50'
      >
        {children}
      </div>
      <button
        type='button'
        aria-haspopup='dialog'
        onClick={gate.open}
        className='group absolute inset-0 flex items-center justify-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryBlue'
      >
        <span className='inline-flex items-center gap-x-2 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 group-hover:bg-gray-50'>
          <LockClosedIcon aria-hidden='true' className='size-4 text-gray-500' />
          {t('pricing.lock.unlock_action')}
          {showPlanNames && <LockPill label={label} size='xs' />}
        </span>
        <span className='sr-only'>{ariaLabel}</span>
      </button>
    </div>
  )
}

LockedSection.propTypes = {
  feature: PropTypes.oneOf(Object.keys(FEATURES)).isRequired,
  source: PropTypes.string,
  inUse: PropTypes.bool,
  allowedAction: PropTypes.node,
  allowedActionPosition: PropTypes.oneOf(['top', 'bottom']),
  className: PropTypes.string,
  children: PropTypes.node.isRequired
}

LockedSection.defaultProps = {
  source: undefined,
  inUse: false,
  allowedAction: null,
  allowedActionPosition: 'bottom',
  className: undefined
}

export default LockedSection
